import '@/assets/scss/app.scss'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'

import toast from '@dogfy/plugins/toast'
import iterable from '@dogfy/plugins/iterable'
import GTM from '@dogfy/plugins/gtm'

import App from '@/App.vue'
import router from '@/router'
import i18n from '@/lang/i18n'
import { useAuthStore } from '@/stores/auth'

const currentEnv = import.meta.env.VUE_APP_ENVIRONMENT
const packageVersion = import.meta.env.APP_VERSION || '0'

const app = createApp(App)

Sentry.init({
  app,
  dsn: 'https://45de35b0a3bd2a9ce3dd5df9a0df1edc@o4508970424270848.ingest.de.sentry.io/4508970437050448',
  release: packageVersion,
  environment: currentEnv,
  enabled: currentEnv !== 'development',
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 0.1,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled.
  tracePropagationTargets: ['https://app.staging.dogfydiet.com', 'https://app.dogfydiet.com'],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  // Ignore errors
  ignoreErrors: [
    'Failed to fetch dynamically imported module',
  ],
  denyUrls: [
    'googletagmanager.com',
    'amplify.outbrain.com',
    'analytics.dogfydiet.com',
    /.*\.css(\?.*)?$/, // Ignore CSS files
    /.*\.(png|jpg|svg)(\?.*)?$/, // Ignore missing assets
  ],
})

const pinia = createPinia()

app.use(pinia)
app.use(i18n)
app.use(toast)
app.use(router)
app.use(iterable, { apiKey: import.meta.env.VUE_APP_ITERABLE_API_KEY })

useAuthStore().init()

router.isReady().then(() => {
  app.use(GTM,
    {
      id: import.meta.env.VUE_APP_GTM_ID,
      pageTracking: import.meta.env.VUE_GTM_PAGE_TRACKING,
      consentAll: true,
      enable: true,
      layer: 'dataLayer',
    }, router, router.currentRoute.value, currentEnv)

  app.mount('#app')
}).catch(err => { console.error(err) })
